import React, { useState, useEffect } from 'react';
import { ShoppingBag, Search, Filter, X } from 'lucide-react';
import ProductCard from '../components/features/ProductCard';
import { productsService } from '../services/api-service';
import './Products.css';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [products, searchQuery, selectedCategory]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const allProducts = await productsService.getAllProducts();
      setProducts(allProducts);
      setFilteredProducts(allProducts);
      
      // Extract unique categories
      const uniqueCategories = [...new Set(allProducts.map(p => p.category).filter(Boolean))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    let filtered = [...products];

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query) ||
        p.category?.toLowerCase().includes(query)
      );
    }

    setFilteredProducts(filtered);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
  };

  return (
    <div className="products-page">
      {/* Header */}
      <section className="products-header">
        <div className="container">
          <div className="products-header-content">
            <ShoppingBag size={48} className="products-header-icon" />
            <h1>Products & Merchandise</h1>
            <p>
              Support Back-2-People and show your community spirit with our exclusive 
              merchandise, digital products, and event packages.
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filters */}
      <section className="products-filters">
        <div className="container">
          <div className="products-filters-content">
            {/* Search */}
            <div className="products-search">
              <Search size={20} className="products-search-icon" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="products-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="products-search-clear"
                  aria-label="Clear search"
                >
                  <X size={18} />
                </button>
              )}
            </div>

            {/* Category Filters */}
            <div className="products-categories">
              <div className="products-categories-label">
                <Filter size={18} />
                <span>Category:</span>
              </div>
              <div className="products-categories-buttons">
                <button
                  className={`products-category-btn ${selectedCategory === 'all' ? 'active' : ''}`}
                  onClick={() => handleCategoryChange('all')}
                >
                  All ({products.length})
                </button>
                {categories.map((category) => (
                  <button
                    key={category}
                    className={`products-category-btn ${selectedCategory === category ? 'active' : ''}`}
                    onClick={() => handleCategoryChange(category)}
                  >
                    {category} ({products.filter(p => p.category === category).length})
                  </button>
                ))}
              </div>
            </div>

            {/* Clear Filters */}
            {(searchQuery || selectedCategory !== 'all') && (
              <button onClick={clearFilters} className="products-clear-filters">
                <X size={16} />
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="products-grid-section section">
        <div className="container">
          {loading ? (
            <div className="products-empty">
              <ShoppingBag size={64} className="products-empty-icon spinning" />
              <h3>Loading products...</h3>
            </div>
          ) : filteredProducts.length > 0 ? (
            <>
              <div className="products-results-info">
                <p>
                  Showing {filteredProducts.length} of {products.length} products
                  {(searchQuery || selectedCategory !== 'all') && (
                    <span className="products-results-filtered">
                      {' '}(filtered)
                    </span>
                  )}
                </p>
              </div>
              <div className="products-grid">
                {filteredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            </>
          ) : (
            <div className="products-empty">
              <ShoppingBag size={64} className="products-empty-icon" />
              <h3>No products found</h3>
              <p>
                {searchQuery || selectedCategory !== 'all' 
                  ? 'Try adjusting your search or filters'
                  : 'Check back soon for new products!'}
              </p>
              {(searchQuery || selectedCategory !== 'all') && (
                <button onClick={clearFilters} className="btn">
                  Clear Filters
                </button>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Products;


